import React, {createContext, useEffect} from 'react';

// Module
import LogCenter, {
	BatchStrategy,
	MemoryStorage,
	RECSender,
	getBatchConfiguration,
} from '@deezer/logcenter';
import config from 'src/config/config.json';

// Hooks
import {
	PreviewerHistoryActions,
	type StreamPayload,
	usePreviewerContext,
} from '@providers/PreviewProvider';

// Metrics
import StreamLog from '@providers/StreamLogProvider/StreamMetrics';
import MdyDebugMetrics from '@providers/StreamLogProvider/MdyDebugMetrics';
import {useGetTokensQuery, usePersonalInfoQuery} from '@graphql/generated';

export type StreamLogProviderState = Record<string, never> | null;

export const LogContext = createContext<StreamLogProviderState>(null);

type StreamLogProviderProps = {
	children?: React.ReactNode;
	streamContext: {id: string; type: string};
};

/**
 * LogCenter config
 * Start
 */
const memoryStorage = new MemoryStorage();

const batchConfiguration = getBatchConfiguration({
	maxBatchSize: 10,
	autoSendInterval: 1 * 60 * 1000,
});

/* ### REC ### */
const recSender = new RECSender(
	`https://${config.host_rec}/1.0/events/`,
	'',
	batchConfiguration,
);

const batchStrategy = new BatchStrategy(
	recSender,
	memoryStorage,
	batchConfiguration,
);

const logCenterInstance = new LogCenter([batchStrategy], memoryStorage);
export const logError = (error: string, userId: string) => {
	const metrics = new MdyDebugMetrics({
		tags: {
			error,
			userId,
		},
	});
	metrics.increment();
	logCenterInstance.log(metrics.serialize());
};

const StreamLogProvider: React.FC<StreamLogProviderProps> = ({
	children,
	streamContext,
}) => {
	const {data: tokensData} = useGetTokensQuery();
	const {data: personalInfoData} = usePersonalInfoQuery();
	const {tracks = {}, dispatch} = usePreviewerContext();

	const flush = batchStrategy.flush.bind(batchStrategy);

	useEffect(() => {
		window.onbeforeunload = () => {
			flush();
		};

		document.onvisibilitychange = () => {
			flush();
		};
	}, []);

	useEffect(() => {
		if (tokensData?.tokens.recToken?.token) {
			recSender.setAuthToken(tokensData?.tokens.recToken.token);
		}
	}, [tokensData]);

	useEffect(() => {
		if (tracks && Object.keys(tracks).length) {
			for (const [key, value] of Object.entries(tracks)) {
				if (value.id && value.startTime) {
					if (value.listeningDuration) {
						logStream(value);
						dispatch({
							type: PreviewerHistoryActions.CLEAR_HISTORY,
							payload: {
								streamId: key,
							},
						});
					}
				}
			}
		}
	}, [tracks]);

	const logStream = (stream: StreamPayload) => {
		if (!tracks || !stream.id) {
			return null;
		}

		const startTime = stream.startTime ?? Date.now();

		logCenterInstance.log(
			new StreamLog({
				createdAt: Math.floor(startTime / 1000),
				isRestricted: true,
				isSynchronized: false,
				listeningDuration: Math.floor(stream.listeningDuration ?? 0),
				listenType: 'music_on_demand',
				media: {
					id: stream.id,
					type: 'song',
				},
				streamContext,
				userId: personalInfoData?.me?.id ?? '',
			}).serialize(),
		);
	};

	return <LogContext.Provider value={{}}>{children}</LogContext.Provider>;
};

export default StreamLogProvider;
